// Настройки подотчёта на карточке лица: порог подсветки и действующая подотчётность.
//
// Тз, раздел 5.5: «Настройки на карточке сотрудника … мягкий лимит остатка на руках, статус
// подотчётности». Заводились они только при создании лица и только через программный интерфейс,
// а карточка при этом писала «порог задаётся в карточке» — и никакого поля рядом не было.
// Следствие видно на живом пульте: у двух самых крупных остатков, 13,3 млн и 235 тысяч, порога
// нет, и строка не краснеет ни при какой сумме, а рядом краснеют семьдесят шесть тысяч. Вся
// подсветка выше порога — то, ради чего пульт вообще красит суммы, — не работала ни у кого,
// кому порог не проставили при заведении.
//
// ## Почему это панель, а не модалка
//
// Порог — не запрет: выдать сверх него можно, он только красит строку. Ошибиться им так, чтобы
// пропали деньги, нельзя, и подтверждения он не требует (правило 4: модалка за необратимым).
//
// ## Почему выключение подотчётности просит основание
//
// Выключенное лицо уходит с пульта вместе со своим остатком. Если остаток не ноль, это решение,
// а не настройка, и в журнале должно остаться слово о том, почему.

import { useState, type FormEvent } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';

import { formatKopecks, formatRubles } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

import { usePerson, useUpdatePersonSettings } from '../queries';

export function SettingsPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const карточка = usePerson(id);
  const правка = useUpdatePersonSettings();

  const [порог, setПорог] = useState<string | null>(null);
  const [действует, setДействует] = useState<boolean | null>(null);
  const [основание, setОснование] = useState('');
  /** Сколько ушло личным долгом при выключении. Пусто — перевода не было. */
  const [переведено, setПереведено] = useState<number | null>(null);
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [готово, setГотово] = useState(false);

  return (
    <ScreenState
      data={карточка.data}
      error={карточка.error as never}
      subject='карточку подотчётного лица'
      isEmpty={() => false}
      onRetry={() => void карточка.refetch()}
      loading={<Skeleton className='h-40 w-full' />}
      empty={null}
    >
      {(лицо) => {
        // Поля берут значение из карточки, пока человек их не трогал: так форма показывает
        // то, что есть сейчас, а не пустоту, из которой непонятно, задано что-нибудь или нет.
        const значениеПорога =
          порог ?? (лицо.soft_limit === null ? '' : formatKopecks(лицо.soft_limit));
        const значениеДействует = действует ?? лицо.is_active;
        const выключаютСДеньгами = !значениеДействует && лицо.balance.unconfirmed_balance !== 0;

        function отправить(событие: FormEvent<HTMLFormElement>) {
          событие.preventDefault();
          const очищенный = значениеПорога.trim();
          const тело: { soft_limit?: number | null; is_active?: boolean; basis?: string } = {};

          if (очищенный === '') {
            тело.soft_limit = null;
          } else {
            const разобранный = parseRublesToKopecks(очищенный);
            if (!разобранный.ok) {
              setОшибка('Порог пишется суммой: 30 000 или 30 000,50. Пусто — порога нет.');
              return;
            }
            тело.soft_limit = разобранный.value;
          }

          if (значениеДействует !== лицо.is_active) {
            тело.is_active = значениеДействует;
          }
          if (выключаютСДеньгами && основание.trim().length < 3) {
            setОшибка(
              'Основание обязательно: человек уходит с пульта вместе с остатком, и без слова о том, почему, этого потом не объяснить.',
            );
            return;
          }
          if (основание.trim() !== '') {
            тело.basis = основание.trim();
          }

          setОшибка(undefined);
          правка.mutate(
            { personId: id, body: тело },
            {
              // Панель не закрывается сама: человек пришёл сюда посмотреть, что стало, и
              // закрывшееся окно ответом не является.
              onSuccess: (карточка) => {
                setПереведено(карточка.carried_to_debt ?? null);
                setГотово(true);
              },
            },
          );
        }

        return (
          <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
            <p className='text-paragraph-xs text-text-sub-600'>{лицо.employee_name}</p>

            <TextField
              id='person-soft-limit'
              label='Порог подсветки'
              value={значениеПорога}
              onChange={(значение) => {
                setПорог(значение);
                setГотово(false);
              }}
              placeholder='30 000'
              hint='Выше него строка на пульте краснеет. Это не запрет: выдать сверх можно. Пусто — порога нет, строка не покраснеет ни при какой сумме.'
              error={ошибка}
            />

            <CheckboxField
              id='person-active'
              label='Подотчётность действует'
              checked={значениеДействует}
              onCheckedChange={(значение) => {
                setДействует(значение);
                setГотово(false);
              }}
              hint='Выключенное лицо уходит с пульта. История выдач и остаток остаются на месте.'
            />

            {выключаютСДеньгами && (
              <>
                <Alert.Root variant='light' status='warning' size='small' role='status'>
                  <Alert.Icon as={RiErrorWarningLine} />
                  <span>
                    На человеке висит {formatRubles(лицо.balance.unconfirmed_balance)}. При
                    выключении эта сумма станет его личным долгом и удержится из окончательного
                    расчёта. Пока по долгу не принято решение — списать или удержать, — рассчитать
                    человека окончательно будет нельзя.
                  </span>
                </Alert.Root>
                <TextField
                  id='person-settings-basis'
                  label='Основание'
                  required
                  value={основание}
                  onChange={setОснование}
                  placeholder='уволен, остаток разбираем отдельно'
                  hint='Попадёт в журнал.'
                />
              </>
            )}

            {правка.isError && <ErrorAlert error={правка.error as never} />}

            {готово && (
              <Alert.Root variant='light' status='success' size='small' role='status'>
                <Alert.Icon as={RiCheckboxCircleLine} />
                <span>
                  {/* Перевод остатка в долг — денежное событие, и молчать о нём нельзя: сумма
                      ушла с пульта, и человек обязан знать, КУДА именно. */}
                  {переведено === null
                    ? `${тексты(лицо.soft_limit)} Пульт обновился: строка красится по новому порогу.`
                    : `Остаток ${formatRubles(переведено)} переведён человеку личным долгом: он удержится из окончательного расчёта. Долг виден в его карточке и в реестре долгов.`}
                </span>
              </Alert.Root>
            )}

            {правка.isPending && (
              <Hint.Root>
                <Hint.Icon as={RiErrorWarningLine} />
                Сохраняем…
              </Hint.Root>
            )}

            <div className='flex items-center gap-2 pt-1'>
              <Button.Root type='submit' variant='primary' mode='filled' size='small'>
                {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
              </Button.Root>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='small'
                onClick={onClose}
              >
                {готово ? 'Закрыть' : 'Отмена'}
              </Button.Root>
            </div>
          </form>
        );
      }}
    </ScreenState>
  );
}

/** Что именно стало с порогом — словами, а не «сохранено». */
function тексты(порог: number | null): string {
  return порог === null
    ? 'Порог снят: строка не покраснеет ни при какой сумме.'
    : `Порог теперь ${formatKopecks(порог)}.`;
}
